import React, { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Slide from '@mui/material/Slide';
import LinearProgress from '@mui/material/LinearProgress';
import { useTheme, alpha } from '@mui/material/styles';
import type { InteractionPart } from '@lib/models/InteractionPart';
import { EventType } from '@lib/models/enums';
import { useAiChatSessionStore } from '../AiChatContext';
import AskUserDispatcher from './parts/AskUserDispatcher';
import { isAskUserPart } from './parts/types';
import { ChevronLeftRounded, ChevronRight, ChevronRightSharp } from '@mui/icons-material';
import { IconButton } from '@mui/material';

interface AskUserSheetProps {
    /** Height of the composer below, so the sheet docks just above it. */
    promptHeight: number;
}

/**
 * Pane-scoped bottom sheet for a PARKED human-input prompt — ask_user (questionnaire) or askauth
 * (sign-in). Both park a turn awaiting the user; this surfaces whichever is pending in the docked drawer.
 *
 * A turn blocked on ask_user goes idle (no tokens stream), so the trailing
 * "..." waiting dots disappear and the inline transcript form reads as a
 * finished turn. This sheet keeps the question unmistakably "your turn": it
 * docks just above the composer, slides up while the turn waits, and leaves
 * the conversation visible/scrollable behind it. The moment the user answers,
 * the tool result lands (functionDetails.status -> 'Done'), the sheet retracts,
 * and the answered prompt renders inline in the transcript (MessageAgent
 * suppresses the parked one inline so it's never in two places).
 *
 * Layout is a flex column: the form scrolls in the middle, and an indeterminate
 * `turnOpen` line is GLUED to the bottom edge OUTSIDE the scroll — the moving
 * cue that the turn is still alive and waiting (replacing the "..." dots that
 * vanish while parked). It renders the very same <AskUserPart> used inline —
 * no duplicated form logic — with `embedded` to drop its own card chrome.
 */
const AskUserSheet: React.FC<AskUserSheetProps> = ({ promptHeight }) => {
    const theme = useTheme();
    const turnOpen = useAiChatSessionStore((s) => s.turnOpen);
    const currentMessage = useAiChatSessionStore((s) => s.currentMessage);
    const [colapsed, setColapsed] = useState(false);

    const parked: InteractionPart | null = useMemo(() => {
        if (!turnOpen || !currentMessage?.parts) return null;
        // Most-recent unanswered ask_user: it parks at status 'Executing' and
        // only reaches 'Done' once the user answers.
        for (let i = currentMessage.parts.length - 1; i >= 0; i--) {
            const p = currentMessage.parts[i];
            if (
                isAskUserPart(p.functionDetails)
                // Wait for the COMPLETE call: while args stream the part is a
                // PartialFunctionCall and the questionnaire JSON won't parse yet —
                // surfacing then shows an empty drawer until the final chunk lands.
                && p.eventType === EventType.FunctionCall
                && p.functionDetails?.status !== 'Done'
            ) {
                return p;
            }
        }
        return null;
    }, [turnOpen, currentMessage]);

    // Keep the last parked part mounted through the slide-out so the sheet
    // doesn't empty before it finishes retracting.
    const [lastParked, setLastParked] = useState<InteractionPart | null>(null);
    useEffect(() => {
        if (parked) setLastParked(parked);
    }, [parked]);
    const toRender = parked ?? lastParked;

    return (
        <Slide direction="up" in={!!parked} mountOnEnter unmountOnExit appear>
            <Box
                sx={{
                    position: 'absolute',
                    left: '7%',
                    right: 8,
                    bottom: promptHeight - 10,
                    zIndex: 1100,
                    display: 'flex',
                    flexDirection: 'column',
                    maxWidth: '85%',
                    maxHeight: '60%',
                    overflow: 'hidden', // clip to the rounded top; inner area owns the scroll
                    backgroundColor: alpha(theme.palette?.background.default,0.6),
                    backdropFilter:'blur(2px)',
                    border: `1px solid ${theme.palette.primary.dark}`,
                    borderTopLeftRadius: 12,
                    borderTopRightRadius: 12,
                    boxShadow: '0 -8px 24px rgba(0,0,0,0.35)',
                }}
            >
                {/* Scrollable content — the form lives here; the line below never scrolls. */}
                <Box sx={{ flex: '1 1 auto', position: "relative", minHeight: 0, maxHeight: colapsed ? 80 : 400,transition: "max-height 0.5s ease", overflowY: colapsed ? 'clip' : 'auto', px: 1.5, pt: 1, pb: 1.25 }}>
                    {/* grabber — reads as a bottom sheet */}
                    <Box sx={{ position: "absolute", left: '45%', width: 36, height: 4, borderRadius: 2, top: 5, bgcolor: 'divider', mb: 1 }} />
                    <IconButton onClick={() => setColapsed(!colapsed)} type="button" sx={{ position: "absolute",m:0,p:0, color: theme.palette.border.soft, right: '1%', top: 0 }}>
                        <ChevronRightSharp sx={{rotate: colapsed ? "-90deg" : "90deg", fontSize: '26px'}} />
                    </IconButton>
                    {toRender && (
                        <AskUserDispatcher part={toRender} message={currentMessage ?? undefined} onGoing={turnOpen} embedded />
                    )}
                </Box>

                {/* turnOpen cue: glued to the bottom edge, full-bleed, OUTSIDE the scroll. */}
                {turnOpen && (
                    <LinearProgress
                        variant="indeterminate"
                        sx={{
                            height: 2,
                            flexShrink: 0,
                            backgroundColor: alpha(theme.palette.primary.dark, 0.15),
                            '& .MuiLinearProgress-bar1': {
                                backgroundColor: theme.palette.primary.main,
                                opacity: 0.5
                            },
                            '& .MuiLinearProgress-bar2': {
                                backgroundColor: theme.palette.primary.dark,
                                opacity: 0.8
                            }
                        }}
                    />
                )}
            </Box>
        </Slide>
    );
};

export default AskUserSheet;
